import { CalendarClock, AlertTriangle } from "lucide-react";

// Parses a DD/MM/YYYY string to a Date (local midnight). Returns null if invalid.
function parseDMY(s) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(s || "").trim());
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return isNaN(d.getTime()) ? null : d;
}

export function getVisaDeadline(app) {
  if (!app || app.service_type !== "visa_extension") return null;
  const expiry = parseDMY(app.form_data?.current_visa_expiry);
  if (!expiry) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((expiry - today) / 86400000);
  let tone = "ok";
  if (days < 0) tone = "expired";
  else if (days <= 7) tone = "urgent";
  else if (days <= 30) tone = "soon";
  return { days, tone, expiry };
}

const TONES = {
  expired: "bg-red-100 text-brand-red border-red-200",
  urgent: "bg-red-100 text-brand-red border-red-200",
  soon: "bg-amber-100 text-brand-amber border-amber-200",
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

// Compact pill for list rows
export function DeadlinePill({ app }) {
  const d = getVisaDeadline(app);
  if (!d) return null;
  const label = d.days < 0 ? `Expired ${Math.abs(d.days)}d ago` : d.days === 0 ? "Expires today" : `${d.days}d left`;
  const Icon = d.tone === "ok" ? CalendarClock : AlertTriangle;
  return (
    <span data-testid="deadline-pill" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${TONES[d.tone]}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

// Full banner for the application detail page
export function DeadlineBanner({ app }) {
  const d = getVisaDeadline(app);
  if (!d) return null;
  const dateStr = d.expiry.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  const msg =
    d.days < 0
      ? `This visa expired ${Math.abs(d.days)} day${Math.abs(d.days) === 1 ? "" : "s"} ago (${dateStr}). Act immediately to avoid further overstay penalties.`
      : d.days === 0
      ? `This visa expires today (${dateStr}). The extension must be lodged now.`
      : `Current visa expires in ${d.days} day${d.days === 1 ? "" : "s"} (${dateStr}). Lodge the extension before this date to avoid an overstay.`;
  return (
    <div data-testid="deadline-banner" className={`mt-4 rounded-2xl border px-5 py-4 flex items-start gap-3 ${TONES[d.tone]}`}>
      {d.tone === "ok" ? <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />}
      <div>
        <p className="text-sm font-bold">Deadline Watch — Visa Extension</p>
        <p className="text-sm mt-0.5">{msg}</p>
      </div>
    </div>
  );
}
